import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuth } from './AuthContext';
import { config } from "../config/appConfig";

// --- Configuration ---
const getSocketUrl = () => {
    try {
        const url = config.websocket?.url;
        if (url) return url;
    } catch (e) {
        // ignore
    }
    if (typeof window !== 'undefined') {
        const proto = window.location.protocol === 'https:' ? 'https:' : 'http:';
        return `${proto}//${window.location.host}/ws`;
    }
    return '/ws';
};

const DEBUG_STOMP = false; // Set to true for very verbose STOMP frame logging

const WebSocketContext = createContext({
    stompClient: null,
    isConnected: false,
    sendMessage: (destination, body) => { console.warn("WS not connected - sendMessage called"); },
    subscribe: (topic, callback) => { console.warn("WS not connected - subscribe called"); return () => { }; },
});

export const WebSocketProvider = ({ children }) => {
    const { isAuthenticated, token, isLoading: isAuthLoading } = useAuth(); // Use Auth context
    const [stompClient, setStompClient] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const subscriptionsRef = useRef({}); // Store active subscriptions { topic: subscriptionObject }
    const clientRef = useRef(null);
    const connectTimerRef = useRef(null); // Debounce timer для защиты от StrictMode double-mount

    // --- Connection Logic ---
    const connect = useCallback(() => {
        if (clientRef.current?.active) {
            // Check if we are upgrading from guest to auth
            const currentToken = clientRef.current.connectHeaders?.Authorization;
            if (isAuthenticated && token && !currentToken) {
                console.log("-> WS: Upgrading from guest to authenticated. Deactivating first.");
                clientRef.current.deactivate();
                // We'll let the next interval/effect handle the reconnection or just fall through
            } else {
                console.log("-> WS connection attempt ignored: Already active.");
                return;
            }
        }
        console.log(`%cAttempting WebSocket connection... auth:${isAuthenticated}`, 'color: blue; font-weight: bold;');

        const connectHeaders = {};
        if (isAuthenticated && token) {
            connectHeaders.Authorization = `Bearer ${token}`;
        }

        const client = new Client({
            webSocketFactory: () => {
                const targetUrl = getSocketUrl();
                return new SockJS(targetUrl);
            },
            connectHeaders: connectHeaders,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: (str) => { if (DEBUG_STOMP) console.debug('STOMP DEBUG: ', str); },
            onConnect: (frame) => {
                console.log('%cSTOMP Connected:', 'color: green; font-weight: bold;', frame);
                setIsConnected(true);
            },
            onDisconnect: () => {
                console.warn('STOMP Disconnected');
                setIsConnected(false);
                subscriptionsRef.current = {};
                clientRef.current = null;
                setStompClient(null);
            },
            onStompError: (frame) => {
                console.error('%cSTOMP Broker Error:', 'color: red; font-weight: bold;', frame.headers['message'], frame);
                setIsConnected(false);
            },
            onWebSocketError: (event) => {
                console.error('%cWebSocket Transport Error:', 'color: red; font-weight: bold;', event);
                setIsConnected(false);
            },
            onWebSocketClose: (event) => {
                console.warn(`WebSocket Closed: Code ${event.code}`);
                setIsConnected(false);
            }
        });

        clientRef.current = client;
        setStompClient(client);

        try {
            console.log("Activating STOMP client...");
            client.activate();
        } catch (error) {
            console.error("Error activating STOMP client:", error);
            setIsConnected(false);
            clientRef.current = null;
            setStompClient(null);
        }
    }, [isAuthenticated, token]); // Depend on auth state and token

    // --- Disconnection Logic ---
    const disconnectWebSocket = useCallback(() => {
        console.log("Attempting explicit WebSocket disconnect...");
        if (clientRef.current) { // Use ref here
            try {
                clientRef.current.deactivate();
                console.log("STOMP client deactivated.");
            } catch (error) {
                console.error("Error during STOMP client deactivation:", error);
            } finally {
                setIsConnected(false);
                subscriptionsRef.current = {};
                clientRef.current = null; // Clear the ref
                setStompClient(null); // Clear state
                console.log("WebSocket state explicitly reset.");
            }
        } else {
            console.log("Explicit disconnect ignored: No active client ref.");
        }
    }, []); // No dependencies needed for explicit disconnect


    // --- Connect/Disconnect based on Auth State ---
    useEffect(() => {
        // Don't try to connect while auth is still loading
        if (isAuthLoading) {
            console.log("WS Effect: Auth is loading, skipping connection check.");
            return;
        }

        // Debounce: откладываем connect на 100мс, чтобы React StrictMode успел
        // завершить свой цикл unmount до того, как мы реально подключимся.
        // Если cleanup вызовется раньше (double-mount), таймер будет отменён.
        console.log("WS Effect: Scheduling connection (Auth: " + isAuthenticated + ")");
        connectTimerRef.current = setTimeout(() => {
            connect();
        }, 100);

        // Cleanup on unmount
        return () => {
            clearTimeout(connectTimerRef.current);
            console.log("WS Effect cleanup: Ensuring disconnection.");
            disconnectWebSocket();
        };
    }, [isAuthenticated, token, connect, disconnectWebSocket, isAuthLoading]); // Add isAuthLoading


    // --- Send Message ---
    const sendMessage = useCallback((logicalDestination, body = {}) => { // Parameter name implies it expects the logical part
        // Prepend the application prefix automatically HERE
        const fullDestination = '/app' + logicalDestination; // <<< KEEP THIS LINE
        const messageBody = JSON.stringify(body);

        if (clientRef.current && clientRef.current.active && isConnected) {
            try {
                // Publish to the *full* destination
                clientRef.current.publish({ destination: fullDestination, body: messageBody });
                console.log(`%cWS Sent OK to ${fullDestination}`, 'color: purple;'); // Log the full path
            } catch (error) {
                console.error(`%cError sending WS message to ${fullDestination}:`, 'color: red;', error);
            }
        } else {
            console.warn(`%cWS Send FAILED: Not connected/active. Dest: ${fullDestination}`, 'color: orange;');
        }
    }, [isConnected]); // isConnected ensures we don't send before STOMP onConnect

    // --- Subscribe to Topic ---
    const subscribe = useCallback((topic, callback) => {
        console.log(`Attempting to subscribe to topic: ${topic}`);
        // Use clientRef.current for subscribing
        if (clientRef.current && clientRef.current.active && isConnected) {
            const subId = topic + '_' + Math.random().toString(36).substring(2, 11);

            console.log(`%cSubscribing to ${topic} (id: ${subId})...`, 'color: cyan;');
            try {
                const subscription = clientRef.current.subscribe(topic, (message) => {
                    console.log(`%c>>> MSG on ${topic}`, 'color: lime;', message.body);
                    try {
                        const parsedBody = JSON.parse(message.body);
                        callback(parsedBody, null);
                    } catch (error) {
                        console.error(`%cError parsing message from ${topic}:`, 'color: red;', error, 'Raw:', message.body);
                        callback(null, error);
                    }
                });
                subscriptionsRef.current[subId] = subscription;
                console.log(`%cSubscribed OK to ${topic}.`, 'color: cyan;');
                // Return unsubscribe function
                return () => {
                    if (subscriptionsRef.current[subId]) {
                        console.log(`%cUnsubscribing from ${topic} (id: ${subId})...`, 'color: magenta;');
                        try { subscriptionsRef.current[subId].unsubscribe(); } catch (e) { console.error("Error unsubscribing:", e); }
                        delete subscriptionsRef.current[subId];
                    }
                };
            } catch (error) {
                console.error(`%cFailed to subscribe to ${topic}:`, 'color: red;', error);
                return () => { };
            }
        } else {
            console.warn(`%cWS Subscribe FAILED: Not connected/active. Topic: ${topic}`, 'color: orange;');
            return () => { };
        }
    }, [isConnected]); // Depend on isConnected

    return (
        <WebSocketContext.Provider value={{ stompClient, isConnected, sendMessage, subscribe }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export default WebSocketContext;